import React, { useState } from 'react';
import { Scale, Plus, X, GraduationCap, DollarSign, MapPin, TrendingUp, CheckCircle, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockColleges } from '../mock';

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

// Get college cost with aid estimates
const getCollegeCost = (collegeName, homeState, income = 80000) => {
  const college = mockColleges[collegeName];
  if (!college) return null;
  
  const isInState = homeState && college.state === homeState;
  const tuition = (isInState && college.tIn) ? college.tIn : college.t;
  const totalCost = tuition + college.h + college.m + college.f;
  
  // Estimate aid based on income
  let estimatedAid = 0;
  if (income <= 30000) estimatedAid = 7395 + 12000;
  else if (income <= 50000) estimatedAid = 5500 + 8000;
  else if (income <= 75000) estimatedAid = 3500 + 5000;
  else if (income <= 100000) estimatedAid = 2000;
  
  return {
    tuition,
    housing: college.h,
    meals: college.m,
    fees: college.f,
    totalCost,
    netCost: Math.max(0, totalCost - estimatedAid),
    isInState,
    acceptanceRate: college.a,
    ranking: college.r,
    state: college.state,
    majors: college.majors
  };
};

const CollegeSelector = ({ value, onChange, onRemove, index }) => {
  const [search, setSearch] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  
  const filtered = Object.keys(mockColleges)
    .filter(c => c.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 8);
  
  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <Input
          placeholder={`Search college ${index + 1}...`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowDropdown(true);
            if (e.target.value !== value) onChange('');
          }}
          onFocus={() => setShowDropdown(true)}
          className="bg-white border-gray-300 text-gray-900 h-11 flex-1"
        />
        {index > 0 && (
          <Button variant="ghost" size="sm" onClick={onRemove} className="text-gray-400 hover:text-red-500">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
      {showDropdown && search && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
          {filtered.map((college) => (
            <div
              key={college}
              className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-gray-900 text-sm"
              onClick={() => {
                setSearch(college);
                onChange(college);
                setShowDropdown(false);
              }}
            >
              {college}
              {mockColleges[college]?.state && (
                <span className="text-gray-500 ml-2">({mockColleges[college].state})</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ComparisonCard = ({ collegeName, data, isLowest, rank }) => {
  if (!data) {
    return (
      <Card className="bg-gray-50 border-gray-200 p-6 flex items-center justify-center min-h-[420px]">
        <p className="text-gray-400 text-center">Select a college to compare</p>
      </Card>
    );
  }
  
  return (
    <Card className={`bg-white border-gray-200 shadow-sm p-6 relative ${isLowest ? 'ring-2 ring-green-500' : ''}`}>
      {isLowest && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
          <Award className="w-3 h-3" /> Best Value
        </div>
      )}
      
      <div className="text-center mb-6 pt-2">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{collegeName}</h3>
        {data.isInState && (
          <span className="inline-flex items-center gap-1 text-green-600 text-sm font-medium">
            <MapPin className="w-3 h-3" /> In-State Tuition
          </span>
        )}
        <p className="text-gray-500 text-sm">{data.state}</p>
      </div>
      
      {/* Key Stats */}
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 rounded-lg text-center">
          <p className="text-gray-600 text-xs uppercase tracking-wide">Est. Net Cost / Year</p>
          <p className="text-3xl font-bold text-blue-600">${data.netCost.toLocaleString()}</p>
          <p className="text-gray-500 text-xs">after estimated aid</p>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg text-center">
            <p className="text-gray-500 text-xs">4-Year Total</p>
            <p className="text-lg font-bold text-gray-900">${(data.netCost * 4).toLocaleString()}</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg text-center">
            <p className="text-gray-500 text-xs">Acceptance</p>
            <p className="text-lg font-bold text-gray-900">{data.acceptanceRate}%</p>
          </div>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex justify-between p-2 bg-gray-50 rounded">
            <span className="text-gray-500">Tuition</span>
            <span className="text-gray-900 font-medium">${data.tuition.toLocaleString()}</span>
          </div>
          <div className="flex justify-between p-2 bg-gray-50 rounded">
            <span className="text-gray-500">Housing</span>
            <span className="text-gray-900 font-medium">${data.housing.toLocaleString()}</span>
          </div>
          <div className="flex justify-between p-2 bg-gray-50 rounded">
            <span className="text-gray-500">Meals</span>
            <span className="text-gray-900 font-medium">${data.meals.toLocaleString()}</span>
          </div>
          <div className="flex justify-between p-2 bg-gray-50 rounded">
            <span className="text-gray-500">Fees</span>
            <span className="text-gray-900 font-medium">${data.fees.toLocaleString()}</span>
          </div>
        </div>
        
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-xs mb-1">Ranking</p>
          <p className="text-gray-900 font-semibold">{data.ranking}</p>
        </div>
      </div>
    </Card>
  );
};

const Compare = () => {
  const [colleges, setColleges] = useState(['', '']);
  const [homeState, setHomeState] = useState('');
  const [familyIncome, setFamilyIncome] = useState('80000');
  const [showResults, setShowResults] = useState(false);
  
  const addCollege = () => {
    if (colleges.length < 4) {
      setColleges([...colleges, '']);
    }
  };
  
  const removeCollege = (index) => {
    setColleges(colleges.filter((_, i) => i !== index));
  };
  
  const updateCollege = (index, value) => {
    const newColleges = [...colleges];
    newColleges[index] = value;
    setColleges(newColleges);
  };
  
  const handleCompare = () => {
    setShowResults(true);
  };
  
  // Get comparison data
  const comparisonData = colleges.map(name => ({
    name,
    data: name ? getCollegeCost(name, homeState, Number(familyIncome)) : null
  }));
  
  // Find lowest cost
  const validData = comparisonData.filter(c => c.data);
  const lowestCost = validData.length > 0 
    ? Math.min(...validData.map(c => c.data.netCost))
    : null;
  
  const highestCost = validData.length > 0 
    ? Math.max(...validData.map(c => c.data.netCost))
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Scale className="w-7 h-7 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Compare Colleges</h1>
            <p className="text-gray-600 max-w-xl mx-auto">
              See costs side-by-side to find your best fit. We'll highlight the best value option.
            </p>
          </div>

          {/* Setup Form */}
          <Card className="bg-white border-gray-200 shadow-sm p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">Your Home State</Label>
                <Select value={homeState} onValueChange={setHomeState}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900 h-11">
                    <SelectValue placeholder="Select for in-state tuition" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-[300px]">
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state} className="text-gray-900">{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 text-sm">Family Income (for aid estimate)</Label>
                <Input
                  type="number"
                  placeholder="80000"
                  value={familyIncome}
                  onChange={(e) => setFamilyIncome(e.target.value)}
                  className="bg-white border-gray-300 text-gray-900 h-11"
                />
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-gray-700 text-sm">Select Colleges to Compare</Label>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {colleges.map((college, index) => (
                  <CollegeSelector
                    key={index}
                    value={college}
                    onChange={(val) => updateCollege(index, val)}
                    onRemove={() => removeCollege(index)}
                    index={index}
                  />
                ))}
                {colleges.length < 4 && (
                  <Button
                    variant="outline"
                    onClick={addCollege}
                    className="border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 h-11"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add College
                  </Button>
                )}
              </div>
            </div>

            <Button
              onClick={handleCompare}
              className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
              disabled={!colleges.some(c => c)}
            >
              Compare Colleges
            </Button>
          </Card>

          {/* Results */}
          {showResults && (
            <div className="space-y-6 animate-in fade-in duration-500">
              {/* Comparison Cards */}
              <div className={`grid gap-6 ${validData.length === 2 ? 'md:grid-cols-2' : validData.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
                {comparisonData.filter(c => c.name).map((item, index) => (
                  <ComparisonCard
                    key={index}
                    collegeName={item.name}
                    data={item.data}
                    isLowest={item.data?.netCost === lowestCost}
                    rank={index + 1}
                  />
                ))}
              </div>

              {/* Summary */}
              {validData.length >= 2 && (
                <Card className="bg-white border-gray-200 shadow-sm p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    Comparison Summary
                  </h2>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-gray-600 text-sm mb-2">Best Value</p>
                      <p className="text-xl font-bold text-green-600">
                        {validData.find(c => c.data.netCost === lowestCost)?.name}
                      </p>
                      <p className="text-gray-500 text-sm">${lowestCost?.toLocaleString()}/year</p>
                    </div>
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-gray-600 text-sm mb-2">4-Year Savings</p>
                      <p className="text-xl font-bold text-blue-600">
                        ${((highestCost - lowestCost) * 4).toLocaleString()}
                      </p>
                      <p className="text-gray-500 text-sm">by choosing best value</p>
                    </div>
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-gray-600 text-sm mb-2">Highest Acceptance</p>
                      <p className="text-xl font-bold text-purple-600">
                        {validData.reduce((a, b) => a.data.acceptanceRate > b.data.acceptanceRate ? a : b).name}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {Math.max(...validData.map(c => c.data.acceptanceRate))}% acceptance
                      </p>
                    </div>
                  </div>

                  {/* Quick Tips */}
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="text-sm font-semibold text-blue-800 mb-2">Quick Tips</h3>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>The "best value" school isn't always the cheapest—consider rankings and your major too.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>Request official financial aid letters from each school before making a final decision.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>You can often negotiate financial aid by showing competing offers.</span>
                      </li>
                    </ul>
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Compare;
